import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hash } from 'lucide-react';
import { Modal } from './ui/Modal';
import { AVATAR_COLORS } from './chatHelpers';
import ImageCropper from './ui/ImageCropper';
import ChannelIconPicker from './chat/ChannelIconPicker';
import ChannelMemberPicker from './chat/ChannelMemberPicker';
import chatService from '../services/chatService';
import './chat/NewChannelModal.css';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

/**
 * "Create channel" modal opened from the chat titlebar "+". Collects a name, an
 * avatar colour (brand palette) or an uploaded image (cropped), and optional
 * people. Creates the channel, uploads the image if present (degrading to the
 * colour-only channel if the avatar endpoint is missing), then hands the
 * channel back via onCreated. The icon and people blocks are their own files.
 */
export default function NewChannelModal({ isOpen, onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [color, setColor] = useState(AVATAR_COLORS[3]);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cropSource, setCropSource] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);

  // Reset on open; revoke any object URL on close/unmount.
  useEffect(() => {
    if (isOpen) {
      setName('');
      setColor(AVATAR_COLORS[3]);
      setFile(null);
      setPreviewUrl(null);
      setCropSource(null);
      setError(null);
      setBusy(false);
      setSelected([]);
    }
  }, [isOpen]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  if (!isOpen) return null;

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > MAX_AVATAR_BYTES) {
      setError(t('chat.newChannelModal.tooLarge'));
      return;
    }
    setError(null);
    setCropSource(f); // open the cropper; the cropped result becomes the upload
  };

  const onCropped = (croppedFile) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(croppedFile);
    setPreviewUrl(URL.createObjectURL(croppedFile));
    setCropSource(null);
  };

  const clearImage = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      let channel = await chatService.createChannel({
        kind: 'channel',
        name: trimmed,
        color,
        memberIds: selected.map((u) => u.id),
      });
      if (file) {
        try {
          const updated = await chatService.uploadChannelAvatar(channel.id, file);
          if (updated) channel = updated;
        } catch {
          /* avatar endpoint unavailable — keep the colour-only channel */
        }
      }
      onCreated?.(channel);
    } catch (e) {
      setError(e.response?.data?.error || t('chat.newChannelModal.createFailed'));
      setBusy(false);
    }
  };

  return (
    <>
      {cropSource && (
        <ImageCropper
          file={cropSource}
          title={t('chat.newChannelModal.cropTitle')}
          saveLabel={t('chat.newChannelModal.cropSave')}
          onCancel={() => setCropSource(null)}
          onCropped={onCropped}
        />
      )}
      <Modal
        isOpen
        onClose={onClose}
        title={t('chat.newChannelModal.title')}
        subtitle={t('chat.newChannelModal.subtitle')}
        contentClassName="ncm"
      >
        <div className="ncm-body">
          <section className="ncm-section">
            <label className="ncm-label" htmlFor="ncm-name">{t('chat.newChannelModal.name')}</label>
            <div className="ncm-field ncm-field--icon">
              <Hash size={14} className="ncm-field-icon" aria-hidden />
              <input
                id="ncm-name"
                type="text"
                autoFocus
                className="modal-invite-input modal-input-flat"
                placeholder={t('chat.newChannelModal.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            </div>
          </section>

          <section className="ncm-section">
            <span className="ncm-label">{t('chat.newChannelModal.icon')}</span>
            <ChannelIconPicker
              name={name}
              color={color}
              onColor={setColor}
              previewUrl={previewUrl}
              hasImage={!!file}
              onPickFile={pickImage}
              onRemove={clearImage}
            />
          </section>

          <section className="ncm-section">
            <span className="ncm-label">
              {t('chat.newChannelModal.people')}
              <span className="ncm-label-optional">{t('chat.newChannelModal.optional')}</span>
            </span>
            <ChannelMemberPicker selected={selected} onChange={setSelected} />
          </section>

          {error && <div className="ncm-error" role="alert">{error}</div>}

          <div className="modal-footer-row">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm">
              {t('chat.newChannelModal.cancel')}
            </button>
            <button type="button" onClick={submit} disabled={!name.trim() || busy} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm">
              {busy ? t('chat.newChannelModal.creating') : t('chat.newChannelModal.create')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
